import { Injectable } from '@angular/core';
import {
  __TYPE__,
  COMMUNICATION_WAY,
  DATE_FORMAT,
  Appointment,
  AppointmentMessage,
  AvailableSlots,
  BasicAppointmentSettings,
  Customer,
  CustomerType,
  DailyCustomerCheckinStatistic,
  DailyIncomeStatistic,
  SumOfDailyIncomeStatistic,
  HEADER_KEY_CONTENT_TYPE,
  HEADER_VALUE_CONTENT_TYPE_TEXT_PLAIN,
  MESSAGE_SENDER_TYPE,
  WEEKDAY,
  AvailableDate,
  AgbAgreement,
  BusinessObject,
  ResponseError,
} from '../../models';
import { CustomerUtils, DateTimeUtils, UrlUtils } from '../../utils';
import { AbstractStateService } from '../state/abstract-state.service';
import { ExchangeTokenServerCallerService } from '../server-caller/exchange-token-server-caller.service';
import { PrivateServerCallerService } from '../server-caller/private-server-caller.service';
import { ServerCallerService } from '../server-caller/server-caller.service';
import { HttpHeaders } from '@angular/common/http';
import { CustomerTypeStatistic } from '../../models/interfaces/CustomerTypeStatistic.interface';

@Injectable({
  providedIn: 'root',
})
export class CustomerService {
  constructor(
    private stateService: AbstractStateService,
    private serverCaller: ServerCallerService,
    private exchangeTokenServerCallerService: ExchangeTokenServerCallerService,
    private privateServerCallerService: PrivateServerCallerService
  ) {}

  // region Customer

  public async getMeAsCustomerOfBusinessObject(
    boId: number
  ): Promise<Customer> {
    const url = '/v1/customer/me';

    const response = await this.exchangeTokenServerCallerService.get(boId, url);
    return response?.body;
  }

  public async updateCustomerUsingExchangeToken(
    boId: number,
    customer: Customer
  ): Promise<boolean> {
    const url = `/v1/customer/${customer.id}`;

    const response = await this.exchangeTokenServerCallerService.put(
      boId,
      url,
      {
        ...customer,
        __type__: __TYPE__.BASIC_CUSTOMER_INFO,
      } as Customer
    );

    return response?.status === 200;
  }

  public async getAllCustomers(): Promise<Customer[]> {
    const url = `/v1/customers`;

    const response = await this.privateServerCallerService.get(url);

    return CustomerUtils.transformCustomerListForFrontend(response?.body);
  }

  public async getCustomerById(customerId: number): Promise<Customer> {
    const url = `/v1/customer/${customerId}`;

    const response = await this.privateServerCallerService.get(url);

    return CustomerUtils.transformCustomerForFrontend(response?.body);
  }

  public async getCustomerByPhoneNumber(
    phoneNumber: string
  ): Promise<Customer> {
    const url = `/v1/customer/${phoneNumber}`;

    const response = await this.privateServerCallerService.get(url);
    return CustomerUtils.transformCustomerForFrontend(response?.body);
  }

  public async getAllCustomersOfBusinessObject(
    businessObjectId: number
  ): Promise<Customer[]> {
    const url = `/v1/business-object/${businessObjectId}/customers`;

    const response = await this.privateServerCallerService.get(url);
    return CustomerUtils.transformCustomerListForFrontend(response?.body);
  }

  public async createAnonymCustomerForBusinessObject(
    businessObject: BusinessObject
  ): Promise<Customer> {
    let anonym: Customer;
    try {
      anonym = await this.getCustomerByPhoneNumber(businessObject.telephone);
    } catch (e: any) {
      const error: ResponseError = e?.error;

      if (error?.status === 404) {
        anonym = await this.createNewCustomerForBusinessObject(
          businessObject.id,
          CustomerUtils.generateCustomerFromKeyword(businessObject.telephone)
        );
      } else {
        throw e;
      }
    }

    return anonym;
  }

  public async createNewCustomerForBusinessObject(
    businessObjectId: number,
    customer: Customer
  ): Promise<Customer> {
    const url = `/v1/business-object/${businessObjectId}/customer`;

    const response = await this.privateServerCallerService.post(
      url,
      CustomerUtils.transformCustomerForBackend(customer)
    );
    return CustomerUtils.transformCustomerForFrontend(response?.body);
  }

  public async updateCustomerById(customer: Customer): Promise<boolean> {
    const url = `/v1/customer/${customer.id}`;

    const response = await this.privateServerCallerService.put(
      url,
      CustomerUtils.transformCustomerForBackend({
        ...customer,
        __type__: __TYPE__.BASIC_CUSTOMER_INFO,
      })
    );
    return response?.status === 200;
  }

  public async acceptAgb(
    boId: number,
    customerId: number,
    agreement: AgbAgreement
  ): Promise<AgbAgreement> {
    const url = `/v1/business-object/${boId}/customer/${customerId}/agb-agreement`;

    const response = await this.privateServerCallerService.post(url, agreement);

    return response?.body;
  }

  public async sendSmsToCustomers(
    businessObjectId: number,
    customerIds: number[],
    smsMessage: string,
    emailMessage: string,
    preferredCommunicationWay?: COMMUNICATION_WAY
  ): Promise<boolean> {
    const url = `/v1/business-object/${businessObjectId}/customers/sms`;

    const dataToSubmit = {
      ids: customerIds,
      message: smsMessage,
      alternativeMessage: emailMessage,
      communicationWay: preferredCommunicationWay || undefined,
    };

    const response = await this.privateServerCallerService.post(
      url,
      dataToSubmit
    );
    return response?.status === 200;
  }

  public async getAllCustomerTypes(): Promise<CustomerType[]> {
    const url = `/v1/customer/types`;

    const response = await this.privateServerCallerService.get(url);
    return response?.body;
  }

  // endregion

  // region Statistic

  public async getCustomerTypeStatisticForBusinessObject(
    businessObjectId: number
  ): Promise<CustomerTypeStatistic> {
    const url = `/v1/business-object/${businessObjectId}/customers/types/statistics`;

    const response = await this.privateServerCallerService.get(url);
    return response?.body;
  }

  public async getCustomerSpentAmountStatisticForBusinessObject(
    businessObjectId: number
  ): Promise<CustomerTypeStatistic> {
    const url = `/v1/business-object/${businessObjectId}/customers/totalSpendAmount/statistics`;

    const response = await this.privateServerCallerService.get(url);
    return response?.body;
  }

  public async getDailyCustomerCheckinStatisticFromDateToDate(
    businessObjectId: number,
    from: Date,
    to: Date
  ): Promise<DailyCustomerCheckinStatistic[]> {
    const fromAsString = DateTimeUtils.formatDateToString(
      from,
      DATE_FORMAT.DATE_FORMAT_BACKEND
    );
    const toAsString = DateTimeUtils.formatDateToString(
      to,
      DATE_FORMAT.DATE_FORMAT_BACKEND
    );

    const url = `/v1/business-object/${businessObjectId}/daily-customer-check-in-statistics/from/${fromAsString}/to/${toAsString}`;

    const response = await this.privateServerCallerService.get(url);
    return response?.body;
  }

  public async getDailyCustomerCheckinStatisticFromDateToNow(
    businessObjectId: number,
    from: Date
  ): Promise<DailyCustomerCheckinStatistic[]> {
    const fromAsString = DateTimeUtils.formatDateToString(
      from,
      DATE_FORMAT.DATE_FORMAT_DE
    );

    const url = `/v1/business-object/${businessObjectId}/daily-customer-check-in-statistics/from/${fromAsString}`;

    const response = await this.privateServerCallerService.get(url);
    return response?.body;
  }

  public async getDailyIncomeStatisticFromDateToDate(
    businessObjectId: number,
    from: Date,
    to: Date
  ): Promise<DailyIncomeStatistic[]> {
    const fromAsString = DateTimeUtils.formatDateToString(
      from,
      DATE_FORMAT.DATE_FORMAT_BACKEND
    );
    const toAsString = DateTimeUtils.formatDateToString(
      to,
      DATE_FORMAT.DATE_FORMAT_BACKEND
    );

    const url = `/v1/business-object/${businessObjectId}/daily-income-statistics/from/${fromAsString}/to/${toAsString}`;

    const response = await this.privateServerCallerService.get(url);
    return response?.body;
  }

  public async getDailyIncomeStatisticFromDateToNow(
    businessObjectId: number,
    from: Date
  ): Promise<DailyIncomeStatistic[]> {
    const fromAsString = DateTimeUtils.formatDateToString(
      from,
      DATE_FORMAT.DATE_FORMAT_BACKEND
    );

    const url = `/v1/business-object/${businessObjectId}/daily-income-statistics/from/${fromAsString}`;

    const response = await this.privateServerCallerService.get(url);
    return response?.body;
  }

  public async getSumOfDailyIncomeStatisticFromDateToDate(
    businessObjectId: number,
    from: Date,
    to: Date
  ): Promise<SumOfDailyIncomeStatistic> {
    const fromAsString = DateTimeUtils.formatDateToString(
      from,
      DATE_FORMAT.DATE_FORMAT_BACKEND
    );
    const toAsString = DateTimeUtils.formatDateToString(
      to,
      DATE_FORMAT.DATE_FORMAT_BACKEND
    );

    const url = `/v1/business-object/${businessObjectId}/daily-income-statistics/sum/from/${fromAsString}/to/${toAsString}`;

    const response = await this.privateServerCallerService.get(url);
    return response?.body;
  }

  public async getSumOfDailyIncomeStatisticFromDateToNow(
    businessObjectId: number,
    from: Date
  ): Promise<SumOfDailyIncomeStatistic> {
    const fromAsString = DateTimeUtils.formatDateToString(
      from,
      DATE_FORMAT.DATE_FORMAT_BACKEND
    );
    const url = `/v1/business-object/${businessObjectId}/daily-income-statistics/sum/from/${fromAsString}`;

    const response = await this.privateServerCallerService.get(url);
    return response?.body;
  }

  // endregion

  // region Appointment

  /**
   * Retrieve all online booking settings of Business object by Id
   * @param   boId :  id of business object
   * @return  BasicAppointmentSettings
   */
  public async getPublicOnlineBookingSettingsOfBo(
    boId: number
  ): Promise<BasicAppointmentSettings> {
    const url = `/v1/business-object/${boId}/appointment-settings`;

    const response = await this.serverCaller.get(url);
    return CustomerUtils.transformBookingSettingForFrontend(response?.body);
  }

  /**
   * Retrieve all online booking settings of Business object by Id
   * @param   boId :  id of business object
   * @return  BasicAppointmentSettings
   */
  public async getOnlineBookingSettingsOfBo(
    boId: number
  ): Promise<BasicAppointmentSettings> {
    const url = `/v1/business-object/${boId}/appointment-settings`;

    const response = await this.privateServerCallerService.get(url);
    return CustomerUtils.transformBookingSettingForFrontend(response?.body);
  }

  public async getAllBookingsForBusinessObject(
    businessObjectId: number,
    dateFrom: Date,
    dateTo: Date
  ): Promise<Appointment[]> {
    const from: string = DateTimeUtils.formatDateToString(
      dateFrom,
      DATE_FORMAT.DATE_FORMAT_BACKEND
    );
    const to: string = DateTimeUtils.formatDateToString(
      dateTo,
      DATE_FORMAT.DATE_FORMAT_BACKEND
    );
    const url = `/v1/business-object/${businessObjectId}/appointment?from=${from}&to=${to}`;

    const response = await this.privateServerCallerService.get(url);

    return response?.body?.map((apb) =>
      CustomerUtils.transformAppointmentForFrontend(apb)
    );
  }

  public async getAllAppointmentsOfBoStartingInNextHours(
    boId: number,
    hours: number
  ): Promise<Appointment[]> {
    const url = `/v1/business-object/${boId}/appointment?startWithin=${hours}`;

    const response = await this.privateServerCallerService.get(url);

    return response?.body?.map((apb) =>
      CustomerUtils.transformAppointmentForFrontend(apb)
    );
  }

  public async getAllAppointmentsOfCustomer(
    boId: number,
    customerId: number
  ): Promise<Appointment[]> {
    const url = `/v1/business-object/${boId}/customer/${customerId}/appointment`;

    const response = await this.privateServerCallerService.get(url);
    return response?.body?.map((apb) =>
      CustomerUtils.transformAppointmentForFrontend(apb)
    );
  }

  public async getAllAppointmentsOfUser(): Promise<Appointment[]> {
    const url = `/v2/user/appointments`;
    const response = await this.privateServerCallerService.get(url);
    return response?.body?.map((a) =>
      CustomerUtils.transformAppointmentForFrontend(a)
    );
  }

  public async getAppointmentByIdAsCustomer(
    appointmentId: number
  ): Promise<Appointment> {
    const url = `/v2/appointment/${appointmentId}`;
    const response = await this.privateServerCallerService.get(url);
    return CustomerUtils.transformAppointmentForFrontend(response?.body);
  }

  public async getAppointmentById(appointmentId: number): Promise<Appointment> {
    const url = `/v1/appointment/${appointmentId}`;

    const response = await this.privateServerCallerService.get(url);
    return CustomerUtils.transformAppointmentForFrontend(response?.body);
  }

  public async getAppointmentByLinkId(linkId: string): Promise<Appointment> {
    const url = `/v1/appointment-public/${linkId}`;

    const response = await this.serverCaller.get(url);
    return CustomerUtils.transformAppointmentForFrontend(response?.body);
  }

  public async createAppointmentAsGuest(
    boId: number,
    appointment: Appointment
  ): Promise<Appointment> {
    const url = `/v1/business-object/${boId}/appointment-public`;

    const response = await this.serverCaller.post(url, {
      ...CustomerUtils.transformAppointmentForBackend(appointment),
      owner: CustomerUtils.transformCustomerForBackend(appointment.owner),
    } as Appointment);

    return CustomerUtils.transformAppointmentForFrontend(response?.body);
  }

  public async createAppointmentAsCustomer(
    boId: number,
    appointment: Appointment
  ): Promise<Appointment> {
    const url = `/v1/appointment`;

    const response = await this.exchangeTokenServerCallerService.post(
      boId,
      url,
      CustomerUtils.transformAppointmentForBackend(appointment)
    );

    return CustomerUtils.transformAppointmentForFrontend(response?.body);
  }

  public async createNewAppointment(
    businessObjectId: number,
    customerId: number,
    appointment: Appointment
  ): Promise<Appointment> {
    const url = `/v1/business-object/${businessObjectId}/customer/${customerId}/appointment`;

    const response = await this.privateServerCallerService.post(
      url,
      CustomerUtils.transformAppointmentForBackend(appointment)
    );
    return CustomerUtils.transformAppointmentForFrontend(response?.body);
  }

  public async cancelAppointmentByLinkId(
    linkId: string,
    message: string
  ): Promise<boolean> {
    const url = `/v1/appointment-public/${linkId}/cancel`;

    const response = await this.serverCaller.post(url, { message });
    return response?.status === 200;
  }

  public async cancelAppointmentAsCustomer(
    boId: number,
    appointmentId: number,
    message: string
  ): Promise<boolean> {
    const url = `/v1/appointment/${appointmentId}/cancel-new`;

    const response = await this.exchangeTokenServerCallerService.post(
      boId,
      url,
      { message }
    );
    return response?.status === 200;
  }

  public async cancelAppointment(
    appointmentId: number,
    message: string
  ): Promise<boolean> {
    const url = `/v1/appointment/${appointmentId}/cancel`;

    const response = await this.privateServerCallerService.post(
      url,
      message,
      new HttpHeaders({
        [HEADER_KEY_CONTENT_TYPE]: HEADER_VALUE_CONTENT_TYPE_TEXT_PLAIN,
      })
    );
    return response?.status === 200;
  }

  public async writeAppointmentMessageAsCustomer(
    boId: number,
    appointmentId: number,
    message: string
  ): Promise<AppointmentMessage> {
    const url = `/v1/appointment/${appointmentId}/message`;

    const response = await this.exchangeTokenServerCallerService.post(
      boId,
      url,
      {
        __type__: __TYPE__.BASIC_APPOINTMENT_MESSAGE,
        text: message,
      } as AppointmentMessage
    );

    return response?.body;
  }

  public async writeAppointmentMessage(
    appointmentId: number,
    message: string
  ): Promise<AppointmentMessage> {
    const url = `/v1/appointment/${appointmentId}/message`;

    const response = await this.privateServerCallerService.post(url, {
      __type__: __TYPE__.BASIC_APPOINTMENT_MESSAGE,
      sender: MESSAGE_SENDER_TYPE.BUSINESS,
      text: message,
    } as AppointmentMessage);

    return response?.body;
  }

  /**
   * Get available time slots to book new appointment
   * @param   {Number}  boId                    : id of business object             (required)
   * @param   {Number}  staffId                 : id of selected staff              (optional)
   * @param   {Number}  appointmentId           : id of appointment                 (optional)
   * @param   {Number}  duration                : duration of all selected service  (required)
   * @param   {String}  from                    : from specific date.               (optional)
   *          If not given, response will be the earliest date containing available slots
   * @param   {String}  to                      : to specific date                  (optional)
   * @param   {Array}   productIds              : list of products ids              (optional)
   * */
  public async findPublicAvailableSlots(
    boId: number,
    staffId: number,
    appointmentId: number,
    duration: number,
    from: string,
    to: string,
    productIds?: number[]
  ): Promise<AvailableSlots> {
    const url = `/v1/business-object/${boId}/available-slots`;
    const endpointWIthParameter = UrlUtils.addParameterToUrl(url, {
      appointmentId,
      duration,
      staffId,
      from,
      to,
      productIds: productIds?.join(','),
    });

    const response = await this.serverCaller.get(endpointWIthParameter);
    return CustomerUtils.transformAvailableSlotsForFrontend(response?.body);
  }

  public async findAvailableSlots(
    boId: number,
    staffId: number,
    appointmentId: number,
    duration: number,
    from: string,
    to: string,
    productIds?: number[]
  ): Promise<AvailableSlots> {
    const url = `/v1/business-object/${boId}/available-slots`;
    const endpointWIthParameter = UrlUtils.addParameterToUrl(url, {
      appointmentId,
      duration,
      staffId,
      from,
      to,
      productIds: productIds?.join(','),
    });

    const response = await this.privateServerCallerService.get(
      endpointWIthParameter
    );
    return CustomerUtils.transformAvailableSlotsForFrontend(response?.body);
  }

  public async findAvailableDates(
    boId: number,
    staffId: number,
    fromDate: string, // YYYY-MM-DD
    toDate: string, // YYYY-MM-DD
    startTime?: string, // HH:MM
    endTime?: string, // HH:MM
    weekdays?: WEEKDAY[]
  ): Promise<AvailableDate[]> {
    const url = `/v1/business-object/${boId}/available-blocks`;

    const endpointWIthParameter = UrlUtils.addParameterToUrl(url, {
      staffId,
      from: fromDate,
      to: toDate,
      start: startTime,
      end: endTime,
      weekdays: weekdays?.join(','),
    });

    const response = await this.privateServerCallerService.get(
      endpointWIthParameter
    );

    return CustomerUtils.transformAvailableDatesForFrontend(response?.body);
  }

  public async updateAppointmentById(
    appointment: Appointment
  ): Promise<boolean> {
    const url = `/v1/appointment/${appointment.id}`;

    const response = await this.privateServerCallerService.put(
      url,
      CustomerUtils.transformAppointmentForBackend(appointment)
    );
    return response?.status === 200;
  }

  public async deleteBlockerById(blockerId: number): Promise<boolean> {
    const url = `/v1/appointment/${blockerId}`;

    const response = await this.privateServerCallerService.delete(url);
    return response?.status === 200;
  }

  public async deleteSerieBlocker(
    blockerId: number,
    serieId: string
  ): Promise<boolean> {
    const url = `/v1/appointment/${blockerId}?serieId=${serieId}`;

    const response = await this.privateServerCallerService.delete(url);
    return response?.status === 200;
  }

  /**
   * Update online booking settings of Business object by Id
   * @param   boId :  id of business object
   * @param   basicAppointmentSettings:  basicAppointmentSettings
   * @return  updated BasicAppointmentSettings
   */
  public async updateOnlineBookingSettingsOfBo(
    boId: number,
    basicAppointmentSettings: BasicAppointmentSettings
  ): Promise<BasicAppointmentSettings> {
    const url = `/v1/business-object/${boId}/appointment-settings`;

    const response = await this.privateServerCallerService.put(
      url,
      basicAppointmentSettings
    );
    return CustomerUtils.transformBookingSettingForFrontend(response?.body);
  }

  // endregion

  // region Exchange token

  public async refreshAllExchangeTokensIfGiven(): Promise<void> {
    const savedBoIds = this.stateService.getAllSavedBoIdForUuidAuthResponse();

    if (!savedBoIds?.length) {
      return;
    }

    await Promise.all(
      savedBoIds?.map((boId) =>
        this.exchangeTokenServerCallerService.exchangeUserTokenToCustomerToken(
          boId
        )
      )
    );
  }

  // endregion
}
